import React from 'react';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { ProgressBar } from '@progress/kendo-react-progressbars';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';

import {
  NotificationContent,
  NotificationContext
} from '../../../contexts/notification';
import { useReels } from '../../../contexts/reels';
import { usePreviewReels, useSaveReelsVideo } from '../../../hooks/use-reels';

export const ProcessVideo: React.FC<{
  eventName: string;
  dialog: string;
  onClose: () => void;
}> = ({ eventName, dialog, onClose }) => {
  const [, setNotification] = React.useContext(NotificationContext);

  const {
    gameCode,
    preview,
    result,
    timeline,
    global_settings,
    versionIdentifier,
    handleChange
  } = useReels();

  const { trigger: triggerSaveVideo } = useSaveReelsVideo(gameCode);

  const { status, progress, loading, data, error, trigger, cancel } =
    usePreviewReels((videoUrl) => {
      handleChange({
        isDirty: false,
        [dialog === 'preview' ? 'preview' : 'result']: {
          url: videoUrl,
          createdAt: new Date().toISOString(),
          versionIdentifier
        }
      });

      if (dialog === 'export') {
        triggerSaveVideo({ s3PresignedUrl: videoUrl }).catch((err) => {
          console.error('Failed to sync reels video to backend:', err);
        });
      }
    });

  const notificationHandler = (message: string) => {
    const content: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>{message}</strong>
        </span>
      )
    };
    setNotification((prevNotification) => ({
      ...prevNotification,
      content: [...prevNotification.content, content]
    }));
  };

  const onProcess = React.useCallback(() => {
    const payload = {
      filename: eventName,
      media: timeline
        .filter(
          (timeline) =>
            timeline.duration_secs > 0 &&
            (timeline.url ||
              timeline.content?.title ||
              timeline.content?.subtitle ||
              timeline.content?.body)
        )
        .map((timeline) => ({
          duration_secs: timeline.duration_secs,
          ...(timeline.url && {
            url: timeline.url
          }),
          ...(timeline.content?.title && {
            title: timeline.content.title
          }),
          ...(timeline.content?.subtitle && {
            subtitle: timeline.content.subtitle
          }),
          ...(timeline.content?.body && {
            body: timeline.content.body
          }),
          ...(timeline.background_color && {
            background_color: timeline.background_color
          })
        })),
      background_color: global_settings.background_color,
      font: global_settings.font,
      quality: dialog === 'preview' ? 'preview' : global_settings.quality,
      transition: {
        duration_secs: global_settings.transition.duration_secs,
        type: global_settings.transition.type
      },
      text_style: global_settings.text_style,
      ...(global_settings.watermark &&
        global_settings.watermark.id &&
        global_settings.watermark.url && {
          watermark: {
            ...global_settings.watermark,
            opacity: parseFloat(global_settings.watermark.opacity)
          }
        })
    };

    trigger(payload);
  }, [eventName, dialog]);

  const isUsePrevious = React.useMemo(() => {
    const getHoursDiff = (startISO: string, endISO: string) => {
      const start = new Date(startISO).getTime();
      const end = new Date(endISO).getTime();
      return (end - start) / (1000 * 60 * 60);
    };

    if (dialog === 'preview' && preview) {
      if (getHoursDiff(preview.createdAt, new Date().toISOString()) > 2)
        return '';
      if (preview.versionIdentifier === versionIdentifier) return preview.url;
    }

    if (dialog === 'export' && result) {
      if (getHoursDiff(result.createdAt, new Date().toISOString()) > 2)
        return '';
      if (result.versionIdentifier === versionIdentifier) return result.url;
    }

    return '';
  }, [preview, result, versionIdentifier]);

  // start process when open
  React.useEffect(() => {
    if (isUsePrevious !== '') return;
    onProcess();
  }, []);

  return (
    <Window
      draggable={false}
      resizable={false}
      modal={true}
      onClose={onClose}
      initialWidth={500}
      initialHeight={500}
      closeButton={() => <></>}
      maximizeButton={() => <></>}
      minimizeButton={() => <></>}
      title={startCase(dialog)}>
      <div className={'d-flex flex-column gap-2'}>
        {/* Status text and progress bar */}
        {loading && (
          <>
            <span>{status}</span>
            <ProgressBar
              style={{
                height: '25px'
              }}
              value={progress}
              label={(props) => (
                <b className={`${progress > 94 ? 'text-white' : 'text-black'}`}>
                  {props.value}%
                </b>
              )}
            />
          </>
        )}

        {/* Error message */}
        {!loading && !data && error && <pre>{error.message}</pre>}

        {/* Result video with action button */}
        {(data || isUsePrevious) && (
          <>
            <video
              src={data || isUsePrevious}
              crossOrigin={'anonymous'}
              preload={'metadata'}
              style={{
                width: '100%',
                maxHeight: '500px',
                borderRadius: '8px'
              }}
              autoPlay
              controls
            />

            <div className={'d-flex gap-2'}>
              <Link to={data} className={'w-100'}>
                <Button themeColor={'success'} className={'w-100'}>
                  Download video
                </Button>
              </Link>
              <Button
                themeColor={'secondary'}
                className={'w-100'}
                onClick={() => {
                  navigator.clipboard.writeText(data || isUsePrevious);
                  notificationHandler('URL copied!');
                }}>
                Copy video url
              </Button>
            </div>
          </>
        )}
      </div>

      <WindowActionsBar>
        {loading ? (
          <Button
            themeColor={'secondary'}
            onClick={() => {
              cancel();
              onClose();
            }}>
            Cancel
          </Button>
        ) : (
          <Button themeColor={'secondary'} onClick={onClose}>
            Close
          </Button>
        )}
        {!loading && !data && error && (
          <Button themeColor={'error'} onClick={onProcess}>
            Retry
          </Button>
        )}
      </WindowActionsBar>
    </Window>
  );
};
